import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSubagents } from "@/hooks/use-agents";
import { useTenant } from "@/context/tenant-context";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { agentRoleLabel } from "@/lib/agent-display";
import { agentStatusLook } from "@/lib/agent-status";
import { AgentAvatar } from "@/components/agent/agent-avatar";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The parent agent's subagents. Same frame as the Access and Tasks tabs
 * (2026-09-30): a heading row with the action on the right, then one bordered
 * box with a row per subagent, and a note underneath.
 *
 * The tab is only shown to people who can manage the agent, and the server's
 * remove route (`requireAgentManage`) allows the same people.
 */
export function AgentSubagentsTab({
  parentAgentId,
  onOpenAgent,
  onCreateSubagent,
}: {
  parentAgentId: string;
  /** Embedded hosts (settings dialog, agent drawer) open the agent in place. */
  onOpenAgent?: (agentId: string) => void;
  /** Embedded hosts open the sub-agent wizard in place. */
  onCreateSubagent?: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: subagents = [], isLoading } = useSubagents(parentAgentId);
  // Which subagent the remove confirmation is open for (null = closed).
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);

  // Same pattern as the Agents list: use the host's callback when there is
  // one, so the popup and drawer don't navigate away to the full page.
  const openAgent = (agentId: string) => {
    if (onOpenAgent) onOpenAgent(agentId);
    else navigate(`/tenants/${tenantId}/agents/${agentId}`);
  };
  const startCreate = () => {
    if (onCreateSubagent) onCreateSubagent();
    else navigate(`/tenants/${tenantId}/agents/${parentAgentId}/subagents/new`);
  };

  const removeMutation = useMutation({
    mutationFn: (subagentId: string) => agentsApi.removeSubagent(parentAgentId, subagentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.agents.detail(parentAgentId), "subagents"] });
    },
  });

  return (
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-text-primary">
          Subagents
          {!isLoading && <span className="ml-1.5 font-normal text-text-muted">· {subagents.length}</span>}
        </h3>
        <Button size="sm" onClick={startCreate}>
          <Plus size={14} className="mr-1.5" />
          Add Subagent
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          {subagents.length === 0 ? (
            // No "Add First Subagent" button here: Add Subagent above does it.
            <p className="px-4 py-3 text-xs text-text-muted">No subagents yet.</p>
          ) : (
            subagents.map((sub) => {
              const model = sub.adapterConfig?.model as string | undefined;
              const look = agentStatusLook(sub.status);
              const role = agentRoleLabel(sub);
              return (
                <div
                  key={sub.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openAgent(sub.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") openAgent(sub.id);
                  }}
                  className="flex cursor-pointer items-center justify-between gap-3 border-b border-border px-4 py-2.5 transition-colors last:border-0 hover:bg-surface-hover"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <AgentAvatar agent={sub} size={32} ringClassName="border-surface" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text-primary">{sub.name}</p>
                      <p className="truncate text-xs text-text-muted">
                        {[role, model].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
                      <span className={cn("h-2 w-2 rounded-full", look.dot)} />
                      {look.label}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      aria-label={`Remove ${sub.name}`}
                      title="Remove"
                      disabled={removeMutation.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmRemove({ id: sub.id, name: sub.name });
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <p className="text-xs text-text-muted">
        Subagents run on this agent's server, so they cost nothing extra.
      </p>

      {/* Remove confirmation: the same in-app dialog as removing a user,
          instead of the browser's own popup. */}
      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setConfirmRemove(null)}
        >
          <div
            className="w-[min(380px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Remove subagent</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Remove{" "}
              <span className="font-medium text-text-primary">{confirmRemove.name}</span>? This
              deletes its workspace from the server and cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" autoFocus onClick={() => setConfirmRemove(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  removeMutation.mutate(confirmRemove.id);
                  setConfirmRemove(null);
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
